import { ASSETS, VideoContent } from '@/modules/Celebrating20YearsPage/content';
import cx from 'classnames';
import Image from 'next/image';
import { useState } from 'react';

import styles from './VideoCard.module.scss';

// youtube-nocookie.com is allowed in this page's CSP frame-src (PAGE_CSP_OVERRIDES in
// utils/contentSecurityPolicy.js)
const YOUTUBE_EMBED_URL = 'https://www.youtube-nocookie.com/embed/';

interface VideoCardProps {
  video: VideoContent;
  variant: 'wide' | 'portrait';
  // Preload the poster when it's likely to be the Largest Contentful Paint element
  priority?: boolean;
}

/**
 * Shows a poster until the video is played, then swaps in the YouTube player, so the page doesn't
 * load YouTube's scripts for videos nobody watches.
 */
const VideoCard = ({ video, variant, priority = false }: VideoCardProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const isWide = variant === 'wide';
  const className = cx(styles.video, isWide ? styles.wide : styles.portrait, {
    [styles.isPlaying]: isPlaying,
  });

  if (isPlaying && video.youtubeId) {
    return (
      <div className={className}>
        <iframe
          className={styles.player}
          src={`${YOUTUBE_EMBED_URL}${encodeURIComponent(video.youtubeId)}?autoplay=1&rel=0`}
          title={video.title}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  const playIcon = (
    <Image
      className={styles.playIcon}
      src={`${ASSETS}/play-button.svg`}
      alt=""
      width={isWide ? 97 : 84}
      height={isWide ? 97 : 84}
    />
  );

  return (
    <div className={className}>
      <Image
        className={styles.poster}
        src={video.poster}
        alt=""
        fill
        priority={priority}
        sizes={isWide ? '(max-width: 1434px) 100vw, 1370px' : '(max-width: 767px) 80vw, 33vw'}
      />
      {video.youtubeId ? (
        <button
          type="button"
          className={styles.play}
          onClick={() => setIsPlaying(true)}
          aria-label={`Play video: ${video.title}`}
        >
          {playIcon}
        </button>
      ) : (
        <span className={styles.play} aria-hidden="true">
          {playIcon}
        </span>
      )}
      <span className={styles.duration}>{video.duration}</span>
    </div>
  );
};

export default VideoCard;
